import React, { useState, useEffect } from 'react'
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Space,
  Typography,
  Radio,
  Card,
  Divider,
  message,
  Alert,
  Row,
  Col,
  Tag,
  Timeline,
  Spin
} from 'antd'
import {
  WhatsAppOutlined,
  MessageOutlined,
  MailOutlined,
  PhoneOutlined,
  SendOutlined,
  HistoryOutlined,
  CloseOutlined
} from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import dayjs from 'dayjs'
import {
  CommunicationTypes,
  CommunicationTemplates,
  sendWhatsAppMessage,
  sendSMSMessage,
  sendEmailMessage,
  recordPhoneCall,
  getCommunicationHistory
} from '../../utils/communicationService'
import { formatCurrency } from '../../utils/helpers'

const { TextArea } = Input
const { Option } = Select
const { Text, Title } = Typography

const CommunicationModal = ({ visible, onCancel, accountData, patientData, onSuccess }) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [communicationType, setCommunicationType] = useState(CommunicationTypes.WHATSAPP)
  const [templateType, setTemplateType] = useState(CommunicationTemplates.PAYMENT_REMINDER)
  const [useTemplate, setUseTemplate] = useState(true)
  const [communicationHistory, setCommunicationHistory] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('send') // 'send' or 'history'

  // Load communication history when modal opens
  useEffect(() => {
    if (visible && accountData?.billingId) {
      loadCommunicationHistory()
    }
  }, [visible, accountData?.billingId])

  const loadCommunicationHistory = async () => {
    setHistoryLoading(true)
    try {
      const history = await getCommunicationHistory(accountData.billingId)
      setCommunicationHistory(history)
    } catch (error) {
      console.error('Failed to load communication history:', error)
    } finally {
      setHistoryLoading(false)
    }
  }

  const templateOptions = [
    {
      value: CommunicationTemplates.PAYMENT_REMINDER,
      label: 'Payment Reminder',
      description: 'Friendly reminder about outstanding balance'
    },
    {
      value: CommunicationTemplates.PAYMENT_REQUEST,
      label: 'Payment Request',
      description: 'Formal request for payment'
    },
    {
      value: CommunicationTemplates.PAYMENT_OVERDUE,
      label: 'Overdue Notice',
      description: 'Urgent notice for overdue accounts'
    },
    {
      value: CommunicationTemplates.PAYMENT_PLAN_OFFER,
      label: 'Payment Plan Offer',
      description: 'Offer payment plan options'
    },
    {
      value: CommunicationTemplates.SETTLEMENT_OFFER,
      label: 'Settlement Offer',
      description: 'Propose settlement amount'
    },
    {
      value: CommunicationTemplates.FINAL_NOTICE,
      label: 'Final Notice',
      description: 'Final notice before collection action'
    },
    {
      value: CommunicationTemplates.MEDICAL_AID_QUERY,
      label: 'Medical Aid Query',
      description: 'Query about medical aid claim status'
    }
  ]

  const getContactInfo = () => {
    if (!patientData) return { phone: '', email: '' }
    return {
      phone: patientData.cellNo || patientData.telNo || '',
      email: patientData.email || ''
    }
  }

  const handleSend = async (values) => {
    setLoading(true)
    try {
      const contactInfo = getContactInfo()
      let result

      const params = {
        templateType: useTemplate ? templateType : null,
        accountData: accountData,
        customMessage: !useTemplate ? values.message : undefined,
        customSubject: !useTemplate ? values.subject : undefined
      }

      switch (communicationType) {
        case CommunicationTypes.WHATSAPP:
          if (!contactInfo.phone) {
            throw new Error('No WhatsApp number available for this patient')
          }
          result = await sendWhatsAppMessage({
            ...params,
            to: contactInfo.phone
          })
          break

        case CommunicationTypes.SMS:
          if (!contactInfo.phone) {
            throw new Error('No SMS number available for this patient')
          }
          result = await sendSMSMessage({
            ...params,
            to: contactInfo.phone
          })
          break

        case CommunicationTypes.EMAIL:
          if (!contactInfo.email) {
            throw new Error('No email address available for this patient')
          }
          result = await sendEmailMessage({
            ...params,
            to: contactInfo.email
          })
          break

        case CommunicationTypes.CALL:
          result = await recordPhoneCall({
            patientPhone: contactInfo.phone,
            accountData: accountData,
            callOutcome: values.callOutcome,
            notes: values.notes,
            duration: values.duration,
            followUpDate: values.followUpDate?.toDate()
          })
          break

        default:
          throw new Error(`Unsupported communication type: ${communicationType}`)
      }

      message.success(
        `${communicationType.toUpperCase()} ${
          communicationType === CommunicationTypes.CALL ? 'recorded' : 'sent'
        } successfully!`
      )

      // Reload communication history
      loadCommunicationHistory()

      // Reset form
      form.resetFields()

      // Call success callback
      if (onSuccess) {
        onSuccess(result)
      }
    } catch (error) {
      console.error('Communication error:', error)
      message.error(`Failed to send ${communicationType}: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const getTypeIcon = (type) => {
    switch (type) {
      case CommunicationTypes.WHATSAPP:
        return <FontAwesomeIcon icon={faWhatsapp} style={{ color: '#25D366' }} />
      case CommunicationTypes.SMS:
        return <MessageOutlined style={{ color: '#1890ff' }} />
      case CommunicationTypes.EMAIL:
        return <MailOutlined style={{ color: '#722ed1' }} />
      case CommunicationTypes.CALL:
        return <PhoneOutlined style={{ color: '#52c41a' }} />
      default:
        return null
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent':
        return 'success'
      case 'failed':
        return 'error'
      case 'completed':
        return 'success'
      case 'no_answer':
        return 'warning'
      case 'busy':
        return 'warning'
      case 'voicemail':
        return 'processing'
      default:
        return 'default'
    }
  }

  const renderSendForm = () => (
    <Form form={form} layout='vertical' onFinish={handleSend}>
      {/* Communication Type Selection */}
      <Form.Item label='Communication Type' required>
        <Radio.Group
          value={communicationType}
          onChange={(e) => setCommunicationType(e.target.value)}
          buttonStyle='solid'
        >
          <Radio.Button value={CommunicationTypes.WHATSAPP}>
            <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp
          </Radio.Button>
          <Radio.Button value={CommunicationTypes.SMS}>
            <MessageOutlined /> SMS
          </Radio.Button>
          <Radio.Button value={CommunicationTypes.EMAIL}>
            <MailOutlined /> Email
          </Radio.Button>
          <Radio.Button value={CommunicationTypes.CALL}>
            <PhoneOutlined /> Call Log
          </Radio.Button>
        </Radio.Group>
      </Form.Item>

      {/* Contact Information Display */}
      <Card size='small' style={{ marginBottom: 16, background: '#f9f9f9' }}>
        <Row gutter={16}>
          <Col span={12}>
            <Text strong>Patient:</Text> {accountData?.patientName || 'Unknown'}
          </Col>
          <Col span={12}>
            <Text strong>Account:</Text> {accountData?.billingId || 'Unknown'}
          </Col>
          <Col span={12} style={{ marginTop: 8 }}>
            <Text strong>Phone:</Text> {getContactInfo().phone || 'Not available'}
          </Col>
          <Col span={12} style={{ marginTop: 8 }}>
            <Text strong>Email:</Text> {getContactInfo().email || 'Not available'}
          </Col>
          <Col span={24} style={{ marginTop: 8 }}>
            <Text strong>Outstanding:</Text>{' '}
            <Text style={{ color: '#cf1322', fontWeight: 'bold' }}>
              {formatCurrency(accountData?.outstandingAmount || 0)}
            </Text>
          </Col>
        </Row>
      </Card>

      {communicationType !== CommunicationTypes.CALL && (
        <>
          {/* Template vs Custom Message */}
          <Form.Item label='Message Type' required>
            <Radio.Group
              value={useTemplate ? 'template' : 'custom'}
              onChange={(e) => setUseTemplate(e.target.value === 'template')}
            >
              <Radio value='template'>Use Template</Radio>
              <Radio value='custom'>Custom Message</Radio>
            </Radio.Group>
          </Form.Item>

          {useTemplate ? (
            <Form.Item label='Template' required>
              <Select value={templateType} onChange={setTemplateType} placeholder='Select a template'>
                {templateOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    <div>
                      <div>{option.label}</div>
                      <Text type='secondary' style={{ fontSize: '12px' }}>
                        {option.description}
                      </Text>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          ) : (
            <>
              {communicationType === CommunicationTypes.EMAIL && (
                <Form.Item
                  name='subject'
                  label='Subject'
                  rules={[{ required: true, message: 'Please enter email subject' }]}
                >
                  <Input placeholder='Enter email subject' />
                </Form.Item>
              )}
              <Form.Item
                name='message'
                label='Message'
                rules={[{ required: true, message: 'Please enter your message' }]}
              >
                <TextArea
                  rows={6}
                  placeholder={`Enter your ${communicationType} message here...

Available variables:
{{patientName}} - Patient's name
{{billingId}} - Account number
{{outstandingAmount}} - Outstanding balance
{{daysOutstanding}} - Days outstanding
{{clinicName}} - Clinic name`}
                />
              </Form.Item>
            </>
          )}
        </>
      )}

      {communicationType === CommunicationTypes.CALL && (
        <>
          <Form.Item
            name='callOutcome'
            label='Call Outcome'
            rules={[{ required: true, message: 'Please select call outcome' }]}
          >
            <Select placeholder='Select call outcome'>
              <Option value='completed'>Call Completed</Option>
              <Option value='no_answer'>No Answer</Option>
              <Option value='busy'>Busy</Option>
              <Option value='voicemail'>Voicemail Left</Option>
            </Select>
          </Form.Item>

          <Form.Item name='duration' label='Duration (minutes)'>
            <Input type='number' placeholder='Call duration in minutes' />
          </Form.Item>

          <Form.Item name='notes' label='Call Notes' rules={[{ required: true, message: 'Please enter call notes' }]}>
            <TextArea rows={4} placeholder='Enter call notes and discussion details...' />
          </Form.Item>
        </>
      )}

      <Form.Item>
        <Space>
          <Button
            type='primary'
            htmlType='submit'
            loading={loading}
            icon={communicationType === CommunicationTypes.CALL ? undefined : <SendOutlined />}
          >
            {communicationType === CommunicationTypes.CALL ? 'Record Call' : 'Send Message'}
          </Button>
          <Button onClick={onCancel}>Cancel</Button>
        </Space>
      </Form.Item>
    </Form>
  )

  const renderHistory = () => (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Title level={5}>Communication History</Title>
        <Text type='secondary'>Recent communications for account {accountData?.billingId}</Text>
      </div>

      {historyLoading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size='large' />
        </div>
      ) : communicationHistory.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Text type='secondary'>No communication history found</Text>
        </div>
      ) : (
        <Timeline>
          {communicationHistory.map((comm, index) => (
            <Timeline.Item key={index} dot={getTypeIcon(comm.type)} color={getStatusColor(comm.status)}>
              <div>
                <Space>
                  <Text strong>{comm.type.toUpperCase()}</Text>
                  <Tag color={getStatusColor(comm.status)}>{comm.status}</Tag>
                  <Text type='secondary'>{dayjs(comm.timestamp).format('DD MMM YYYY HH:mm')}</Text>
                </Space>
                <div style={{ marginTop: 4 }}>
                  <Text>To: {comm.recipient}</Text>
                </div>
                {comm.subject && (
                  <div style={{ marginTop: 4 }}>
                    <Text strong>Subject:</Text> {comm.subject}
                  </div>
                )}
                <div style={{ marginTop: 4 }}>
                  <Text>{comm.message}</Text>
                </div>
                {comm.error && (
                  <div style={{ marginTop: 4 }}>
                    <Text type='danger'>Error: {comm.error}</Text>
                  </div>
                )}
              </div>
            </Timeline.Item>
          ))}
        </Timeline>
      )}
    </div>
  )

  return (
    <Modal
      title={
        <Space>
          {getTypeIcon(communicationType)}
          <span>Patient Communication - {accountData?.patientName}</span>
        </Space>
      }
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <Radio.Group value={activeTab} onChange={(e) => setActiveTab(e.target.value)} buttonStyle='solid'>
          <Radio.Button value='send'>
            <SendOutlined /> Send Communication
          </Radio.Button>
          <Radio.Button value='history'>
            <HistoryOutlined /> History
          </Radio.Button>
        </Radio.Group>
      </div>

      <Divider style={{ margin: '16px 0' }} />

      {activeTab === 'send' ? renderSendForm() : renderHistory()}
    </Modal>
  )
}

export default CommunicationModal
